import { AxiosError } from 'axios';
import { Form, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { requisitesApi } from 'api';
import {
  CloseDialogResult,
  CloseFormikDialogResult,
  FormikCheckbox,
  FormikDialog,
} from 'components';
import { useMutation, usePayinRequisitesUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Requisites } from 'types';

type Values = {
  disableFutureVerification: boolean;
};

type Props = {
  open: boolean;
  requisites?: Requisites;
  onClose: (result: CloseDialogResult) => void;
};

export const ManagerRequisitesVerificationDialog: React.FC<Props> = ({
  open,
  requisites,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'requisites_verification_dialog',
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        disableFutureVerification: Yup.boolean(),
      }),
    [],
  );

  const { isVerificationDisabled } = usePayinRequisitesUtils();

  const requisitesVerificationDisabled = useMemo(
    () => isVerificationDisabled(requisites),
    [requisites, isVerificationDisabled],
  );

  const initialValues: Values = useMemo(
    () => ({
      disableFutureVerification: requisitesVerificationDisabled,
    }),
    [requisitesVerificationDisabled],
  );

  const verifyMutation = useMutation<
    Requisites,
    AxiosError,
    { id: string; disableFutureVerification: boolean }
  >(requisitesApi.verify, {
    notifierType: 'execute',
  });

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      if (requisites) {
        verifyMutation.mutate(
          {
            id: requisites.id,
            disableFutureVerification: values.disableFutureVerification,
          },
          {
            onSettled: () => {
              formikHelpers.setSubmitting(false);
            },
            onSuccess: () => {
              formikHelpers.resetForm();
              onClose({ ok: true });
            },
          },
        );
      }
    },
    [requisites, verifyMutation, onClose],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        handleSubmit(result.data.values, result.data.formikHelpers);
      } else {
        verifyMutation.reset();
        result.data?.formikHelpers.resetForm();
        onClose({ ok: result.ok });
      }
    },
    [verifyMutation, onClose, handleSubmit],
  );

  return (
    <FormikDialog
      dialogProps={{
        open,
        title: t('title'),
        mutation: verifyMutation,
        onClose: handleClose,
      }}
      formikProps={{
        validationSchema,
        initialValues,
        isInitialValid: true,
      }}
    >
      {() => (
        <Form>
          <div className="tw-mb-4">{t('message')}</div>
          <div>
            <FormikCheckbox
              label={t('fields.disable_future_verification')}
              name="disableFutureVerification"
            />
          </div>
          <div className="tw-text-gray-500">
            {t('fields.disable_future_verification_helper')}
          </div>
        </Form>
      )}
    </FormikDialog>
  );
};
